import type { Theme } from '@material-ui/core'
import { makeStyles, useTheme } from '@material-ui/core'
import type { ButtonProps as MuiButtonProps } from '@material-ui/core/Button'
import { default as MuiButton } from '@material-ui/core/Button'
import type { SvgIconProps } from '@material-ui/core/SvgIcon'
import classnames from 'classnames'
import React from 'react'
import type { StyleClasses } from '../../styles'
import { scaleVariant } from '../../styles'
import Spinner from '../Spinner'

const useStyles = makeStyles<Theme, ButtonProps>((theme: Theme) => ({
  // Plain rule, not a props-function — dynamic JSS rules insert later than static ones and can
  // still win a same-specificity tie against dialogButtonRoot on mount, even when gated (#21962).
  root: {
    ...theme.typography.button,
    borderRadius: theme.borderRadius.md,
    whiteSpace: 'nowrap',
    boxShadow: 'none',
    padding: '0.6em 1.0em',
    position: 'relative',
    overflow: 'hidden'
  },
  inputHeight: {
    height: 42,
    [theme.breakpoints.down('sm')]: {
      height: 39
    }
  },
  // `!important` on both states here, not just source-order gating — this stylesheet still mixes
  // static and dynamic (props-function) rules (dialog/underlineRoot/textNoPadding's color/etc.),
  // so react-jss still attaches it lazily and a same-specificity background from another variant
  // class (e.g. contained) can still win a mount-time insertion-order tie against a plain
  // (non-important) background here (#21962 recurrence after #22043). !important removes this
  // rule from that race entirely — the only remaining tie is against itself, where the higher-
  // specificity `&:hover` selector always wins on its own.
  dialogButtonRoot: {
    background: 'transparent !important',
    color: theme.paintscout.palette.primary,
    border: `1px solid ${theme.paintscout.palette.primary}`,
    '@media (hover: hover) and (pointer: fine)': {
      '&:hover': {
        background: `${theme.paintscout.palette.primary} !important`,
        color: theme.palette.common.white
      }
    }
  },
  dialogButtonError: {
    background: `${theme.palette.error.light} !important`,
    color: theme.palette.error.main,
    border: `1px solid ${theme.palette.error.main}`,
    '@media (hover: hover) and (pointer: fine)': {
      '&:hover': {
        color: theme.palette.common.white,
        background: `${theme.paintscout.palette.primary} !important`,
        border: `1px solid ${theme.paintscout.palette.primary}`
      }
    }
  },
  underlineRoot: (props) => ({
    backgroundColor: 'transparent !important',
    '&:hover':
      props.variant === 'underline'
        ? {
            color: theme.palette.primary.main
          }
        : {}
  }),
  contained: {
    color: theme.palette.primary.contrastText,
    background: theme.palette.primary.main,
    '&$disabled': {
      background: theme.paintscout.palette.brand.Neutral.P90,
      transition: 'none'
    },
    '&:hover': {
      // PaintScout blue darkens to Blue50 on hover (ENG-2094). A company's own primary colour (the
      // customer view) keeps its flat hover, since there is no darker shade of it in the palette.
      backgroundColor:
        theme.palette.primary.main.toLowerCase() === theme.paintscout.palette.brand.Blue.Blue55
          ? theme.paintscout.palette.brand.Blue.Blue50
          : theme.palette.primary.main
    }
  },
  disabled: {},
  outlined: {
    background: 'none',
    color: theme.palette.primary.main,
    border: 'dashed',
    borderWidth: '2px',
    borderColor: theme.paintscout.palette.brand.Neutral.P85,
    '&$disabled': {
      border: 'dashed',
      borderWidth: '2px'
    },
    '&:hover': {
      boxShadow: 'none',
      background: 'none',
      border: 'dashed',
      borderWidth: '2px',
      borderColor: 'none'
    }
  },
  tabText: {
    ...theme.typography.subtitle2,
    background: 'none'
  },
  tab: {
    backgroundColor: theme.paintscout.palette.brand.Neutral.P96,
    border: `1px solid transparent`,
    '&:hover': {
      backgroundColor: theme.paintscout.palette.brand.Neutral.P96,
      border: `1px solid ${theme.paintscout.palette.primary}`,
      boxShadow: 'none'
    },
    '& span': {
      color: theme.palette.text.primary
    }
  },
  secondaryTab: {
    backgroundColor: theme.palette.common.white,
    color: theme.palette.text.primary,
    display: 'flex',
    justifyContent: 'flex-start',
    width: '100%',
    '& span': {
      color: theme.paintscout.palette.brand.Neutral.P60
    }
  },
  text: {
    background: 'none',
    color: theme.paintscout.palette.brand.Neutral.P50
  },
  textPrimary: {
    background: 'none',
    color: theme.palette.primary.main
  },
  textSecondary: {
    background: 'none',
    color: theme.palette.secondary.main
  },
  sizeSmall: {
    padding: '0.3em 0.6em',
    ...scaleVariant(theme.typography.button, 0.8)
  },
  sizeLarge: {
    padding: '0.75em 1.5em',
    ...scaleVariant(theme.typography.button, 1.3)
  },
  label: {
    display: 'flex',
    alignItems: 'center',
    '& span': {
      display: 'flex'
    }
  },
  dialog: (props) => ({
    ...(props.disabled
      ? {
          borderColor: theme.paintscout.palette.brand.Neutral.P85
        }
      : {})
  }),
  underline: (props) => {
    if (props.variant === 'underline') {
      return {
        '& span': {
          borderBottom: '2px solid'
        }
      }
    } else if (props.variant === 'hoverUnderline') {
      return {
        '&:hover': {
          '& span': {
            borderBottom: '2px solid',
            marginBottom: '-2px'
          }
        }
      }
    }
  },
  textNoPadding: {
    background: 'none',
    color: ({ color }) => color ?? theme.paintscout.palette.brand.Neutral.P50,
    padding: 0,
    overflow: 'visible',
    border: 'none',
    outline: 'none',
    boxShadow: 'none',
    '&:focus': {
      outline: 'none',
      boxShadow: 'none'
    },
    '@media (hover: hover) and (pointer: fine)': {
      '&:hover': {
        background: 'none',
        color: theme.palette.primary.main,
        border: 'none',
        boxShadow: 'none'
      }
    }
  },
  loading: {
    '& $startIcon': {
      opacity: 0
    },
    '& $endIcon': {
      opacity: 0
    }
  },
  startIcon: (props) => ({
    '& svg': {
      fontSize: `${props?.iconSize ?? '1.0em'} !important`
    },
    borderBottom: 'none !important',
    marginBottom: '0 !important'
  }),
  endIcon: (props) => ({
    '& svg': {
      fontSize: `${props?.iconSize ?? '1.0em'} !important`
    }
  }),
  spinnerWrapper: {
    opacity: '1 !important',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0
  },
  buttonSpan: {
    whiteSpace: 'nowrap'
  },
  spinner: {},
  roundedWhite: {
    background: theme.paintscout.palette.primary,
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    textTransform: 'none',
    letterSpacing: 0,
    borderRadius: theme.borderRadius.md,
    '& span': {
      fontWeight: theme.typography.fontWeightMedium,
      color: theme.palette.common.white
    },
    '&:hover': {
      background: theme.paintscout.palette.primary,
      boxShadow: theme.shadows[3]
    }
  },
  'active-tab': {
    backgroundColor: theme.palette.primary.main,
    '& span': {
      color: `${theme.palette.common.white}`
    },
    '&:hover': {
      backgroundColor: theme.palette.primary.dark
    },
    '&:active': {
      backgroundColor: theme.palette.primary.main
    },
    '&:focus': {
      backgroundColor: theme.palette.primary.main,
      border: `1px solid ${theme.paintscout.palette.primary}`,
      boxShadow: 'none'
    },
    '&:focus-visible': {
      backgroundColor: theme.palette.primary.main,

      border: `1px solid ${theme.paintscout.palette.primary}`,
      boxShadow: 'none'
    }
  },
  'active-secondaryTab': {
    backgroundColor: theme.paintscout.palette.secondary[230].C95,
    '& span': {
      color: `${theme.paintscout.palette.primary}`
    }
  },
  eventComplete: {
    backgroundColor: theme.paintscout.palette.brand.Neutral.White,
    color: theme.paintscout.palette.brand.Neutral.P50,
    fontWeight: theme.typography.fontWeightLight,
    border: `1px solid ${theme.paintscout.palette.brand.Neutral.P85}`
  },
  'active-eventComplete': {
    backgroundColor: theme.paintscout.palette.semantic.Success.S70,
    color: theme.paintscout.palette.brand.Neutral.White,
    '&:hover': {
      backgroundColor: theme.paintscout.palette.semantic.Success.S60
    }
  }
}))

export interface ButtonProps extends Omit<MuiButtonProps, 'variant'> {
  classes?: StyleClasses<typeof useStyles>
  children?: React.ReactNode
  loading?: boolean
  component?: React.ElementType

  /**
   * Icon to show on the button
   */
  icon?: React.ComponentType<SvgIconProps>

  /**
   * Icon is shown on the left by default. If this is true, icon will show on right.
   */
  flipIconSide?: boolean

  active?: boolean

  variant?:
    | ButtonVariant
    | 'underline'
    | 'hoverUnderline'
    | 'roundedWhite'
    | 'textNoPadding'
    | 'dialog'
    | 'tab'
    | 'secondaryTab'
    | 'eventComplete'
  iconSize?: string
  error?: boolean
  inputHeight?: boolean
  prominence?: number
}
type ButtonVariant = 'contained' | 'outlined' | 'text'

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { icon, flipIconSide, active, children, error, inputHeight, loading, ...props },
  ref
) {
  const classes = useStyles({ loading, ...props })
  const { loading: loadingClass, spinnerWrapper: spinnerWrapperClass, spinner: spinnerClass, buttonSpan } = classes
  const theme = useTheme()

  const underline = props.variant === 'underline' || props.variant === 'hoverUnderline'
  const textNoPadding = props.variant === 'textNoPadding'
  const dialogButton = props.variant === 'dialog'
  const Icon = icon
  const disabled = !!(props.disabled || loading)

  return (
    <MuiButton
      startIcon={icon && !flipIconSide ? <Icon /> : null}
      endIcon={icon && flipIconSide ? <Icon /> : null}
      {...props}
      variant={underline || textNoPadding ? 'text' : (props.variant as ButtonVariant)}
      disableRipple={underline || textNoPadding || props.disableRipple}
      disabled={disabled}
      classes={{
        root: classnames(
          classes.root,
          loading && loadingClass,
          inputHeight && classes.inputHeight,
          underline && classes.underlineRoot,
          dialogButton && (error ? classes.dialogButtonError : classes.dialogButtonRoot),
          error && classes.error,
          classes[props.variant],
          active && classes[`active-${props.variant}`]
        ),
        label: classnames(
          classes.label,
          underline && classes.underline,
          textNoPadding && classes.textNoPadding,
          ['tab', 'secondaryTab'].includes(props.variant) && classes.tabText
        ),
        contained: classes.contained,
        outlined: classes.outlined,
        text: classes.text,
        textPrimary: classes.textPrimary,
        textSecondary: classes.textSecondary,
        disabled: classes.disabled,
        sizeSmall: classes.sizeSmall,
        sizeLarge: classes.sizeLarge,
        startIcon: classes.startIcon,
        endIcon: classes.endIcon
      }}
      ref={ref}
    >
      <span className={buttonSpan} aria-hidden={loading} style={loading ? { opacity: 0 } : {}}>
        {children}
      </span>
      {loading && (
        <div className={spinnerWrapperClass} data-testid="button-spinner">
          <Spinner
            fullWidth
            fullHeight
            size={15}
            color={props.variant === 'contained' ? '#fff' : theme.palette.primary.main}
            className={spinnerClass}
          />
        </div>
      )}
    </MuiButton>
  )
})

Button.defaultProps = {
  variant: 'contained',
  flipIconSide: false,
  loading: false
}

export default Button
